import React, { createContext, useContext, useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { createNextMocaTheme } from "./theme";
import type { NextMocaThemeMode } from "./theme";
import type { TenantBrandingInput } from "./branding";

type ThemeModeContextValue = {
  mode: NextMocaThemeMode;
  toggleMode: () => void;
  setMode: (mode: NextMocaThemeMode) => void;
};

const STORAGE_KEY = "nextmoca-theme-mode";

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: "light",
  toggleMode: () => {},
  setMode: () => {},
});

function readInitialMode(): NextMocaThemeMode {
  if (typeof window === "undefined") {
    return "light";
  }
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "dark" || stored === "light") {
    return stored;
  }
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function NextMocaThemeProvider({
  children,
  branding,
}: {
  children: React.ReactNode;
  branding?: TenantBrandingInput;
}) {
  const [mode, setModeState] = useState<NextMocaThemeMode>(readInitialMode);
  const theme = useMemo(
    () => createNextMocaTheme(mode, branding),
    [mode, branding]
  );

  const value = useMemo<ThemeModeContextValue>(() => {
    const setMode = (nextMode: NextMocaThemeMode) => {
      setModeState(nextMode);
      window.localStorage.setItem(STORAGE_KEY, nextMode);
    };
    return {
      mode,
      setMode,
      toggleMode: () => setMode(mode === "dark" ? "light" : "dark"),
    };
  }, [mode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  return useContext(ThemeModeContext);
}
